import { cva } from 'class-variance-authority';
import { forwardRef, ReactNode, useCallback, useRef, useState } from 'react';
import { type NumberFormatValues, NumericFormat } from 'react-number-format';
import { cn } from './utils';

const agInputWrapperVariants = cva('inline-flex items-center w-full bg-white border transition-all duration-150', {
  variants: {
    variant: { default: 'border-neutral-5 hover:border-neutral-6', error: 'border-red-text' },
    size: {
      sm: 'h-[32px] px-2.5 gap-1.5 text-14',
      md: 'h-[40px] px-3 gap-2 text-14',
      lg: 'h-[48px] px-4 gap-2.5 text-16',
    },
    shape: { rounded: 'rounded-md', pill: 'rounded-full' },
    isFocused: { true: '', false: '' },
    isDisabled: { true: 'opacity-60 pointer-events-none bg-neutral-2', false: '' },
  },
  compoundVariants: [
    { variant: 'default', isFocused: true, className: 'border-brand-6 ring-2 ring-ring-primary' },
    { variant: 'error', isFocused: true, className: 'ring-2 ring-ring-destructive' },
  ],
});

const agInputVariants = cva(
  'flex-1 bg-transparent outline-none text-neutral-text placeholder:text-neutral-alpha-7 min-w-0',
  { variants: { size: { sm: 'text-14', md: 'text-14', lg: 'text-16' } } }
);

const agLabelVariants = cva('text-neutral-text font-medium', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-14' } },
});

export interface AgNumericInputProps {
  /** Numeric value. null means empty. */
  value: number | null;
  /** Called with the numeric value, or null if cleared */
  onChange: (value: number | null) => void;
  /** Label text displayed above the input */
  label?: string;
  /** Show red asterisk for required fields */
  isRequired?: boolean;
  /** Show "(optional)" indicator next to label */
  isOptional?: boolean;
  /** Helper text displayed below the input */
  helperText?: string;
  /** Error message to display (also sets error state) */
  errorMessage?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Content to render before the input */
  startContent?: ReactNode;
  /** Content to render after the input */
  endContent?: ReactNode;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Shape variant */
  shape?: 'rounded' | 'pill';
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Whether the input has an error state */
  isInvalid?: boolean;
  /** Allow negative values */
  allowNegative?: boolean;
  /** Max decimal places (default: no limit) */
  decimalScale?: number;
  /** Use thousand separator (default: false) */
  thousandSeparator?: boolean;
  /** Additional class for the outer container */
  className?: string;
  /** Additional class for the wrapper container */
  wrapperClassName?: string;
  /** Additional class for the input element */
  inputClassName?: string;
}

export const AgNumericInput = forwardRef<HTMLInputElement, AgNumericInputProps>(
  (
    {
      value,
      onChange,
      label,
      isRequired,
      isOptional,
      helperText,
      errorMessage,
      placeholder,
      startContent,
      endContent,
      size = 'md',
      shape = 'rounded',
      isDisabled = false,
      isInvalid,
      allowNegative = false,
      decimalScale,
      thousandSeparator = false,
      className,
      wrapperClassName,
      inputClassName,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasError = isInvalid || !!errorMessage;
    const internalRef = useRef<HTMLInputElement>(null);
    const setRefs = useCallback(
      (el: HTMLInputElement | null) => {
        internalRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      },
      [ref]
    );

    const handleValueChange = useCallback(
      (values: NumberFormatValues) => {
        if (values.value === '' || values.value === '-') {
          onChange(null);
        } else {
          const num = values.floatValue;
          onChange(num !== undefined ? num : null);
        }
      },
      [onChange]
    );

    return (
      <div className={cn('flex flex-col gap-2 w-full', className)}>
        {label && (
          <label className={cn(agLabelVariants({ size }))}>
            {label}
            {isRequired && <span className="text-red-text ml-2">*</span>}
            {isOptional && <span className="text-neutral-alpha-7 font-normal ml-4">(optional)</span>}
          </label>
        )}
        <div
          className={cn(
            agInputWrapperVariants({ variant: hasError ? 'error' : 'default', size, shape, isFocused, isDisabled }),
            wrapperClassName
          )}
        >
          {startContent && <span className="flex items-center shrink-0 text-neutral-alpha-8">{startContent}</span>}
          <NumericFormat
            getInputRef={setRefs}
            value={value ?? ''}
            onValueChange={handleValueChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            thousandSeparator={thousandSeparator ? ',' : undefined}
            decimalScale={decimalScale}
            allowNegative={allowNegative}
            placeholder={placeholder}
            disabled={isDisabled}
            className={cn(agInputVariants({ size }), inputClassName)}
          />
          {endContent && <span className="flex items-center shrink-0 text-neutral-alpha-8">{endContent}</span>}
        </div>
        {errorMessage && <span className="text-12 text-red-text">{errorMessage}</span>}
        {helperText && !errorMessage && <span className="text-12 text-neutral-alpha-7">{helperText}</span>}
      </div>
    );
  }
);

AgNumericInput.displayName = 'AgNumericInput';
